import Delete from "@mui/icons-material/Delete";
import List from "@mui/icons-material/List";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import Grid2 from "@mui/material/Grid2";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import classNames from "classnames";
import _ from "lodash";
import React, { useState } from "react";
import { Controller } from "react-hook-form";
import {
  ExtractionType,
  ParsingType,
  PredefinedExtraction,
  RegexMatch,
} from "../../../interfaces/doc";
import { ExtractionQuerySchemaType } from "../../../schemas/OrionConfig";
import FormTextField from "../../Common/FormTextField";
import ExtractionIcon from "./ConfigExtractionIcons";
import {
  useConfigModeContext,
  useFormConfigContext,
} from "./ConfigFormContext";
import CustomExtractionForm from "./CustomExtractionForm";
import EnumField from "./EnumField";
import SingleMany from "./SingleMany";

interface ConfigExtractionQueryProps {
  index: number;
  groupIndex?: number;
  removeQuery: Function;
}

const ConfigExtractionQuery: React.FC<ConfigExtractionQueryProps> = ({
  index,
  groupIndex,
  removeQuery,
}) => {
  const { control, setValue, watch } = useFormConfigContext();
  const { isReadOnly } = useConfigModeContext();
  const [open, setOpen] = useState<boolean>(false);
  const queryPath =
    groupIndex !== undefined
      ? (`config.extraction.queries.${index}.queries.${groupIndex}` as const)
      : (`config.extraction.queries.${index}` as const);

  const query = watch(`${queryPath}`) as ExtractionQuerySchemaType;

  const getContent = () => {
    if (query.type === ExtractionType.PREDEFINED)
      if (!open) {
        return (
          <>
            <div className="flex gap-2 w-full items-center justify-between">
              <Typography fontSize={16} fontWeight={600}>
                {query.name}
              </Typography>
              <div className="flex gap-2 items-center">
                <Typography fontWeight={200}>{query.type}</Typography>
                {!isReadOnly && (
                  <Button color="error" onClick={() => removeQuery()}>
                    <Delete color="error" />
                  </Button>
                )}
              </div>
            </div>
            <Typography fontSize={12}>{query.query}</Typography>
          </>
        );
      } else {
        return (
          <Stack rowGap={4}>
            <div className="flex gap-2">
              <FormTextField
                control={control}
                name={`${queryPath}.name`}
                placeholder="Nom de l'extraction"
                variant="standard"
                fullWidth
                slotProps={{
                  input: {
                    style: { fontSize: 20, fontWeight: 600 },
                  },
                }}
              />
              <Button onClick={() => setOpen(false)}>OK</Button>
            </div>
            <FormControl>
              <FormLabel id="query_label">Choix de l'extraction</FormLabel>
              <Controller
                control={control}
                name={`${queryPath}.query`}
                render={({ field }) => (
                  <Select
                    size="small"
                    className="flex flex-1"
                    labelId="query_label"
                    {...field}
                  >
                    {_.values(PredefinedExtraction).map((queryType) => (
                      <MenuItem key={queryType} value={queryType}>
                        {queryType}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </Stack>
        );
      }
    else if (query.type === ExtractionType.LLM)
      if (!open) {
        return (
          <>
            <div className="flex gap-2 w-full items-center justify-between">
              <Typography fontSize={16} fontWeight={600}>
                {query.name}
              </Typography>
              <div className="flex gap-2 items-center">
                <Typography fontWeight={200}>{query.type}</Typography>
                {!isReadOnly && (
                  <Button color="error" onClick={() => removeQuery()}>
                    <Delete color="error" />
                  </Button>
                )}
              </div>
            </div>
            <div className="flex gap-2 w-full items-center">
              <ExtractionIcon
                parsingType={query.parsing_type ?? ParsingType.String}
                props={{ fontSize: "small" }}
              />
              {query.many && <List fontSize="small" />}
              <Typography fontSize={12} className="truncate">
                {query.query}
              </Typography>
            </div>
          </>
        );
      } else {
        return (
          <Stack rowGap={4}>
            <div className="flex gap-2">
              <FormTextField
                control={control}
                name={`${queryPath}.name`}
                placeholder="Nom de l'extraction"
                variant="standard"
                fullWidth
                slotProps={{
                  input: {
                    style: { fontSize: 20, fontWeight: 600 },
                  },
                }}
              />
              <Button onClick={() => setOpen(false)}>OK</Button>
            </div>
            <Grid2 container spacing={2}>
              <Grid2 size={10}>
                <Controller
                  control={control}
                  name={`${queryPath}.query`}
                  render={({ field: queryField }) => (
                    <TextField
                      {...queryField}
                      fullWidth
                      size="small"
                      multiline
                      label="Extraction"
                      placeholder="Décrivez l'information que vous souhaitez extraire"
                      slotProps={{
                        input: {
                          startAdornment: (
                            <Controller
                              control={control}
                              name={`${queryPath}.parsing_type`}
                              render={({ field: selectField }) => (
                                <Select
                                  size="small"
                                  variant="standard"
                                  disableUnderline
                                  sx={{
                                    minWidth: 120,
                                    borderTopRightRadius: 0,
                                    borderBottomRightRadius: 0,
                                    borderRight: "1px solid #e0e0e0",
                                    marginRight: 1,
                                  }}
                                  {...selectField}
                                  onChange={(e) => {
                                    if (e.target.value === ParsingType.Custom) {
                                      setValue(`${queryPath}.custom_schema`, [
                                        {
                                          name: "",
                                          parsing_type: ParsingType.String,
                                        },
                                      ]);
                                    } else {
                                      setValue(
                                        `${queryPath}.custom_schema`,
                                        undefined
                                      );
                                    }
                                    if (e.target.value === ParsingType.Enum) {
                                      setValue(`${queryPath}.enum_values`, []);
                                    } else {
                                      setValue(
                                        `${queryPath}.enum_values`,
                                        undefined
                                      );
                                    }
                                    selectField.onChange(e.target.value);
                                  }}
                                >
                                  {_.values(ParsingType).map((parsingType) => (
                                    <MenuItem
                                      key={parsingType}
                                      value={parsingType}
                                    >
                                      <div className="flex items-center gap-1">
                                        <ExtractionIcon
                                          parsingType={parsingType}
                                          props={{ fontSize: "small" }}
                                        />
                                        {parsingType.charAt(0).toUpperCase() +
                                          parsingType.slice(1)}
                                      </div>
                                    </MenuItem>
                                  ))}
                                </Select>
                              )}
                            />
                          ),
                        },
                      }}
                    />
                  )}
                />
              </Grid2>
              <Grid2 size={2}>
                <SingleMany isReadOnly={isReadOnly} queryPath={queryPath} />
              </Grid2>
              {"parsing_type" in query &&
                query.parsing_type === ParsingType.Enum && (
                  <Grid2 size={12}>
                    <EnumField
                      isReadOnly={isReadOnly}
                      queryPath={`${queryPath}.enum_values`}
                    />
                  </Grid2>
                )}
              {"parsing_type" in query &&
                query.parsing_type === ParsingType.Custom && (
                  <Grid2 size={12}>
                    <CustomExtractionForm
                      isReadOnly={isReadOnly}
                      queryPath={queryPath}
                    />
                  </Grid2>
                )}
            </Grid2>
          </Stack>
        );
      }
    else if (query.type === ExtractionType.REGEX)
      if (!open) {
        return (
          <>
            <div className="flex gap-2 w-full items-center justify-between">
              <Typography fontSize={16} fontWeight={600}>
                {query.name}
              </Typography>
              <div className="flex gap-2 items-center">
                <Typography fontWeight={200}>{query.type}</Typography>
                {!isReadOnly && (
                  <Button color="error" onClick={() => removeQuery()}>
                    <Delete color="error" />
                  </Button>
                )}
              </div>
            </div>
            <div className="flex gap-2 w-full items-center">
              <ExtractionIcon
                parsingType={query.parsing_type ?? ParsingType.String}
                props={{ fontSize: "small" }}
              />
              {query.many && <List fontSize="small" />}
              <Typography fontSize={12} className="truncate">
                {query.pattern}
              </Typography>
            </div>
          </>
        );
      } else {
        return (
          <Stack rowGap={4}>
            <div className="flex gap-2">
              <FormTextField
                control={control}
                name={`${queryPath}.name`}
                placeholder="Nom de l'extraction"
                variant="standard"
                fullWidth
                slotProps={{
                  input: {
                    style: { fontSize: 20, fontWeight: 600 },
                  },
                }}
              />
              <Button onClick={() => setOpen(false)}>OK</Button>
            </div>
            <Grid2 container spacing={2}>
              <Grid2 size={10}>
                <Controller
                  control={control}
                  name={`${queryPath}.pattern`}
                  render={({ field: queryField }) => (
                    <TextField
                      {...queryField}
                      fullWidth
                      size="small"
                      multiline
                      label="Expression régulière"
                      slotProps={{
                        input: {
                          startAdornment: (
                            <Controller
                              control={control}
                              name={`${queryPath}.parsing_type`}
                              render={({ field: selectField }) => (
                                <Select
                                  size="small"
                                  variant="standard"
                                  disableUnderline
                                  sx={{
                                    minWidth: 120,
                                    borderTopRightRadius: 0,
                                    borderBottomRightRadius: 0,
                                    borderRight: "1px solid #e0e0e0",
                                    marginRight: 1,
                                  }}
                                  {...selectField}
                                  onChange={(e) => {
                                    if (e.target.value === ParsingType.Custom) {
                                      setValue(`${queryPath}.custom_schema`, [
                                        {
                                          name: "",
                                          parsing_type: ParsingType.String,
                                        },
                                      ]);
                                    } else {
                                      setValue(
                                        `${queryPath}.custom_schema`,
                                        undefined
                                      );
                                    }
                                    if (e.target.value === ParsingType.Enum) {
                                      setValue(`${queryPath}.enum_values`, []);
                                    } else {
                                      setValue(
                                        `${queryPath}.enum_values`,
                                        undefined
                                      );
                                    }
                                    selectField.onChange(e.target.value);
                                  }}
                                >
                                  {_.values(ParsingType).map((parsingType) => (
                                    <MenuItem
                                      key={parsingType}
                                      value={parsingType}
                                    >
                                      <div className="flex items-center gap-1">
                                        <ExtractionIcon
                                          parsingType={parsingType}
                                          props={{
                                            fontSize: "small",
                                          }}
                                        />
                                        {parsingType.charAt(0).toUpperCase() +
                                          parsingType.slice(1)}
                                      </div>
                                    </MenuItem>
                                  ))}
                                </Select>
                              )}
                            />
                          ),
                        },
                      }}
                    />
                  )}
                />
              </Grid2>
              <Grid2 size={2}>
                <SingleMany isReadOnly={isReadOnly} queryPath={queryPath} />
              </Grid2>

              {"parsing_type" in query &&
                query.parsing_type === ParsingType.Enum && (
                  <Grid2 size={12}>
                    <EnumField
                      isReadOnly={isReadOnly}
                      queryPath={`${queryPath}.enum_values`}
                    />
                  </Grid2>
                )}
              {"parsing_type" in query &&
                query.parsing_type === ParsingType.Custom && (
                  <Grid2 size={12}>
                    <CustomExtractionForm
                      isReadOnly={isReadOnly}
                      queryPath={queryPath}
                    />
                  </Grid2>
                )}
              <Grid2 size={6}>
                <Controller
                  control={control}
                  name={`${queryPath}.params.match`}
                  render={({ field }) => (
                    <Select
                      size="small"
                      labelId="query_label"
                      fullWidth
                      {...field}
                      onChange={(e) => {
                        if (e.target.value === RegexMatch.ALL) {
                          setValue(`${queryPath}.many`, true);
                        } else {
                          setValue(`${queryPath}.many`, false);
                        }
                        if (e.target.value === RegexMatch.EXISTS) {
                          setValue(
                            `${queryPath}.parsing_type`,
                            ParsingType.Boolean
                          );
                        }
                        field.onChange(e.target.value);
                      }}
                    >
                      {_.values(RegexMatch).map((match) => (
                        <MenuItem key={match} value={match}>
                          {match}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </Grid2>
              <Grid2 size={6}>
                <FormTextField
                  fullWidth
                  size="small"
                  label="Numéro du groupe"
                  name={`${queryPath}.params.group_number`}
                  handleChange={(e, fieldOnChange: (e: any) => void) =>
                    fieldOnChange(
                      e.target.value
                        ? Number(e.target.value.replace(/\D/, ""))
                        : null
                    )
                  }
                  control={control}
                  type="number"
                  rules={{ valueAsNumber: true }}
                />
              </Grid2>
            </Grid2>
          </Stack>
        );
      }
    else if (
      query.type === ExtractionType.COMPUTED ||
      query.type === ExtractionType.AGGREGATE
    )
      if (!open) {
        return (
          <>
            <div className="flex gap-2 w-full items-center justify-between">
              <Typography fontSize={16} fontWeight={600}>
                {query.name}
              </Typography>
              <div className="flex gap-2 items-center">
                <Typography fontWeight={200}>{query.type}</Typography>
                {!isReadOnly && (
                  <Button color="error" onClick={() => removeQuery()}>
                    <Delete color="error" />
                  </Button>
                )}
              </div>
            </div>
            <div className="flex gap-2 w-full items-center">
              <ExtractionIcon
                parsingType={query.parsing_type ?? ParsingType.String}
                props={{ fontSize: "small" }}
              />
              <Typography fontSize={12} className="truncate">
                {query.expression}
              </Typography>
            </div>
          </>
        );
      } else {
        return (
          <Stack rowGap={4}>
            <div className="flex gap-2">
              <FormTextField
                control={control}
                name={`${queryPath}.name`}
                placeholder="Nom de l'extraction"
                variant="standard"
                fullWidth
                slotProps={{
                  input: {
                    style: { fontSize: 20, fontWeight: 600 },
                  },
                }}
              />
              <Button onClick={() => setOpen(false)}>OK</Button>
            </div>
            <Grid2 container spacing={2}>
              <Grid2 size={12}>
                <Controller
                  control={control}
                  name={`${queryPath}.expression`}
                  render={({ field: queryField }) => (
                    <TextField
                      {...queryField}
                      fullWidth
                      size="small"
                      multiline
                      label="Expression"
                      placeholder="Décrivez l'information que vous souhaitez extraire en JSONata"
                      slotProps={{
                        input: {
                          startAdornment: (
                            <Controller
                              control={control}
                              name={`${queryPath}.parsing_type`}
                              render={({ field: selectField }) => (
                                <Select
                                  size="small"
                                  variant="standard"
                                  disableUnderline
                                  sx={{
                                    minWidth: 120,
                                    borderTopRightRadius: 0,
                                    borderBottomRightRadius: 0,
                                    borderRight: "1px solid #e0e0e0",
                                    marginRight: 1,
                                  }}
                                  {...selectField}
                                  onChange={(e) => {
                                    if (e.target.value === ParsingType.Custom) {
                                      setValue(`${queryPath}.custom_schema`, [
                                        {
                                          name: "",
                                          parsing_type: ParsingType.String,
                                        },
                                      ]);
                                    } else {
                                      setValue(
                                        `${queryPath}.custom_schema`,
                                        undefined
                                      );
                                    }
                                    selectField.onChange(e.target.value);
                                  }}
                                >
                                  {_.values(ParsingType)
                                    .filter(
                                      (parsingType) =>
                                        parsingType !== ParsingType.Enum
                                    )
                                    .map((parsingType) => (
                                      <MenuItem
                                        key={parsingType}
                                        value={parsingType}
                                      >
                                        <div className="flex items-center gap-1">
                                          <ExtractionIcon
                                            parsingType={parsingType}
                                            props={{ fontSize: "small" }}
                                          />
                                          {parsingType.charAt(0).toUpperCase() +
                                            parsingType.slice(1)}
                                        </div>
                                      </MenuItem>
                                    ))}
                                </Select>
                              )}
                            />
                          ),
                        },
                      }}
                    />
                  )}
                />
              </Grid2>
              {"parsing_type" in query &&
                query.parsing_type === ParsingType.Custom && (
                  <Grid2 size={12}>
                    <CustomExtractionForm
                      isReadOnly={isReadOnly}
                      queryPath={queryPath}
                    />
                  </Grid2>
                )}
            </Grid2>
          </Stack>
        );
      }
    else return null;
  };

  return (
    <Paper
      square={false}
      elevation={3}
      className={classNames(
        "flex flex-col gap-2 p-2 border-[var(--mui-palette-primary-main)] border-solid	border-1",
        {
          "hover:cursor-pointer": !open,
        }
      )}
      onClick={!open ? () => setOpen(true) : undefined}
    >
      {getContent()}
    </Paper>
  );
};
export default ConfigExtractionQuery;
